import React from 'react';
import { Icon } from '@repo/ui';
import { Button, Card, CardHeader, CardBody } from '@repo/ui';
import { WEBHOOK_MODELS } from '../constants';

interface WebhookModelSelectorProps {
  selectedModel: string;
  selectedEvents: string[];
  onModelChange: (model: string) => void;
  onEventsChange: (events: string[]) => void;
  onCreate: () => void;
  isCreating: boolean;
}

export const WebhookModelSelector: React.FC<WebhookModelSelectorProps> = ({
  selectedModel,
  selectedEvents,
  onModelChange,
  onEventsChange,
  onCreate,
  isCreating,
}) => {
  const handleEventToggle = (event: string, checked: boolean) => {
    if (checked) {
      onEventsChange([...selectedEvents, event]);
    } else {
      onEventsChange(selectedEvents.filter(ev => ev !== event));
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white flex items-center">
          <Icon name="HiPlus" className="mr-2" />
          Create Clio Webhook
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Creates webhook in Clio and automatically enables processing with SQS queues and Lambda
          triggers
        </p>
      </CardHeader>
      <CardBody>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-xtnd-dark dark:text-xtnd-white mb-2">
              Model Type
            </label>
            <select
              value={selectedModel}
              onChange={e => onModelChange(e.target.value)}
              className="w-full px-3 py-2 border border-xtnd-light rounded-md focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:border-transparent bg-white dark:bg-xtnd-dark text-xtnd-dark dark:text-xtnd-white"
              disabled={isCreating}
            >
              <option value="">Select a model...</option>
              {WEBHOOK_MODELS.map(model => (
                <option key={model.name} value={model.name}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-xtnd-dark dark:text-xtnd-white mb-2">
              Events
            </label>
            <div className="space-y-2">
              {selectedModel &&
                WEBHOOK_MODELS.find(m => m.name === selectedModel)?.events.map(event => (
                  <label key={event} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(event)}
                      onChange={e => handleEventToggle(event, e.target.checked)}
                      className="mr-2 text-xtnd-blue focus:ring-xtnd-blue"
                      disabled={isCreating}
                    />
                    <span className="text-sm text-xtnd-dark dark:text-xtnd-white">{event}</span>
                  </label>
                ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <Icon name="HiCog" className="inline mr-1" />
            Auto-processing will be enabled automatically
          </div>
          <Button
            onClick={onCreate}
            disabled={!selectedModel || selectedEvents.length === 0 || isCreating}
            loading={isCreating}
            variant="primary"
            leftIcon={<Icon name="HiPlus" size="sm" />}
          >
            Create Webhook
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

